import { MaterialIcons } from "@expo/vector-icons";
import type { NativeStackNavigationProp } from "@react-navigation/native-stack";
import _ from "lodash";
import {
  Actionsheet,
  Box,
  Button,
  Center,
  Checkbox,
  HStack,
  Icon,
  Radio,
  ScrollView,
  Spinner,
  Text,
  useTheme,
  VStack,
} from "native-base";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { FlatList, SafeAreaView, StyleSheet, Text as RNText, View } from "react-native";
import { useSelector } from "react-redux";

import { CommonSearchInput } from "../commons";
import CommonHeaderDivider from "../commons/CommonHeaderDivider";
import RecipeMealCard from "../components/RecipeCard"; // Assuming this card can be reused
import { commonStyles, Routes, Scale } from "../constants";
import { FilterActionsheetComponent, useRecipeInspirationFilters } from "../hooks/useRecipeInspirationFilters";
import type { RootStackParamList } from "../navigation/NavigationStackParams";
import backendApi from "../services/backendApi"; // Need to check which hook to use for general recipes
import type {
  FoodRecipeInspirationListApiArg,
  GeneratedRecipeMeal,
  InspirationRecipeTemplate,
  MealSlotSpecification,
  RecipeTemplate,
} from "../services/backendTypes";
import { api } from "../services/baseApi"; // For potential prefetching
import logger from "../services/logger";
import { userSelector } from "../slices/userSlice"; // May or may not be needed depending on API

// Use the new hook for fetching inspiration recipes.
const { useFoodRecipeInspirationListQuery } = backendApi;

// Create a dummy MealSlotSpecification as RecipeDetailScreen requires it
const dummyMealSlotSpecification: MealSlotSpecification = {
  id: 0,
  meal_type: "SNACK", // Use a default, won't be displayed meaningfully here
  kcal: 0, // Dummy value
  protein: 0, // Dummy value
  fat: 0, // Dummy value
  carbohydrates: 0, // Dummy value
  order: 0,
  meal_moment: "BREAKFAST",
  nutrition_day_plan: -1,
};

// Filter types - adjust based on backend expectations
type FavoriteFilter = "favorite" | "no_favorite" | null;
type EatingTimeFilter = FoodRecipeInspirationListApiArg["mealTypes"]; // Use type from backendTypes
// Update PrepTimeFilter to accept only a single string or null
type PrepTimeFilter = string | null;

const SEARCH_INPUT_DEBOUNCE_TIME_MS = 300;

type Props = {
  navigation: NativeStackNavigationProp<RootStackParamList, Routes.RecipeInspirationScreen>;
};

// Basic structure, needs refinement for actual data fetching and display
const RecipeInspirationScreen = ({ navigation }: Props): JSX.Element => {
  const { t: screenT } = useTranslation();
  const theme = useTheme();
  const user = useSelector(userSelector);

  const [page, setPage] = useState<number>(1);
  const [searchText, setSearchText] = useState("");
  const [debouncedSearchText, setDebouncedSearchText] = useState("");
  const [allRecipes, setAllRecipes] = useState<InspirationRecipeTemplate[]>([]);

  // Filter State
  const [selectedFavorite, setSelectedFavorite] = useState<FavoriteFilter>(null);
  const [selectedEatingTimes, setSelectedEatingTimes] = useState<EatingTimeFilter>([]);
  // Update state for single selection or null
  const [selectedPrepTimes, setSelectedPrepTimes] = useState<PrepTimeFilter>(null);

  // Callback for filter changes
  const resetPageAndClear = useCallback(() => {
    setPage(1);
  }, []);

  // Use the custom filter hook - get props instead of component
  const {
    filterQueryArgs,
    openFilterSheet,
    actionsheetProps,
    t: filterHookT,
  } = useRecipeInspirationFilters(resetPageAndClear);

  // Debounce handler for search input
  const debouncedSetSearch = useCallback(
    _.debounce((value: string) => {
      setDebouncedSearchText(value);
    }, SEARCH_INPUT_DEBOUNCE_TIME_MS),
    []
  );

  // Effect to trigger reset when debounced search text changes
  useEffect(() => {
    // This is safe: resetPageAndClear is defined via useCallback([]) and only calls
    // a stable state setter (setPage), so there's no 'this' context issue.
    // eslint-disable-next-line @typescript-eslint/unbound-method
    resetPageAndClear();
  }, [debouncedSearchText, resetPageAndClear]);

  // Construct final query args
  const finalQueryArgs: FoodRecipeInspirationListApiArg = useMemo(() => {
    const params: FoodRecipeInspirationListApiArg = {
      page,
      pageSize: 20,
      ingredientSearch: debouncedSearchText || undefined,
      ...filterQueryArgs,
    };
    return _.omitBy(params, (value) => _.isUndefined(value));
  }, [page, debouncedSearchText, filterQueryArgs]);

  // RTK Query Hook
  const { data, error, isLoading: isLoadingFirstTime, isFetching } = useFoodRecipeInspirationListQuery(finalQueryArgs);

  // Effect to handle data loading and pagination
  useEffect(() => {
    if (!isFetching) {
      if (page === 1) {
        setAllRecipes(data?.results ?? []);
      } else if (data?.results) {
        setAllRecipes((prev) => {
          const existingIds = new Set(prev.map((r) => r.id));
          const uniqueNewRecipes = data.results.filter((r) => !existingIds.has(r.id));
          return [...prev, ...uniqueNewRecipes];
        });
      }
    }
  }, [data, isFetching, page]);

  // Log errors
  useEffect(() => {
    if (error) {
      logger.error("Error fetching recipe inspiration:", error);
    }
  }, [error]);

  const onPressBack = (): void => {
    navigation.goBack();
  };

  const onSearchTextChange = (value: string): void => {
    setSearchText(value);
    debouncedSetSearch(value);
  };

  const onRecipePress = (recipe: InspirationRecipeTemplate): void => {
    // Navigate to the stack containing RecipeDetailScreen first

    // Ensure portions from the serialized recipe template is used, default to 1 if undefined
    const portionsToShow = recipe.portions ?? 1;

    navigation.push(Routes.AddRecipeTemplateStack, {
      // @ts-expect-error The navigation types have always been a mystery to me
      screen: Routes.RecipeDetailScreen,
      params: {
        // Pass the RecipeTemplate. RecipeDetailScreen might expect GeneratedRecipeMeal,
        // so adapt as needed (similar to how we adapted for RecipeMealCard)
        recipeMeal: {
          recipe_template: recipe,
          id: recipe.id ?? 0, // recipe.id should still be number here
          kcal: recipe.kcal,
          protein: recipe.protein,
          fat: recipe.fat,
          carbohydrates: recipe.carbohydrates,
          // Add other potential fields RecipeDetailScreen might minimally expect from GeneratedRecipeMeal
          portions: portionsToShow, // Use the portions from the API response
          portions_multiplier: 1,
          planned_portions: null,
          directions: recipe.directions,
          ingredients: recipe.ingredients, // Pass ingredients if available on RecipeTemplate type
        },
        mealSlotSpecification: dummyMealSlotSpecification, // Pass the dummy spec
        // mealType: undefined, // Not relevant here
        // recipeTemplateEditable: true, // Or determine based on context
        // onSelect: undefined, // No selection action needed from inspiration screen
        viewOnly: true, // Set viewOnly true as we are just browsing
        editable: false, // Cannot edit from inspiration screen
        recipeTemplateEditable: false, // Cannot edit template from inspiration screen
        portionsEditable: false, // Cannot edit portions from inspiration screen
      },
    });
  };

  // Use the openFilterSheet function from the hook
  const onFiltersPress = (): void => {
    logger.info("Filters button pressed");
    openFilterSheet(); // Use the function returned by the hook
  };

  const headerComponent = useMemo(
    () => (
      <View>
        {/* Simplified Header */}
        <View style={styles.pageHeader}>
          {/* Optional Back Button */}
          <Text style={[commonStyles.titleText24, { textAlign: "center", flex: 1, paddingVertical: Scale(15) }]}>
            {screenT("bottom_tabs.recipes_tab_name")}
          </Text>
        </View>

        <CommonHeaderDivider />

        <View style={commonStyles.paddingContainer}>
          <View style={{ paddingTop: Scale(10) }}>
            <CommonSearchInput
              placeholder={screenT("recipe_search.search_prompt")} // Add translation key
              onChangeText={onSearchTextChange}
              value={searchText} // Controlled component
              leftIcon={<Icon as={MaterialIcons} mx={"2"} size={"md"} color={"primary.600"} name="search" />}
              testID={"recipe-inspiration-search-input"}
            />
          </View>
          <View style={{ marginVertical: Scale(15), alignItems: "flex-start" }}>
            <Button
              variant="outline"
              leftIcon={<Icon as={MaterialIcons} name="filter-list" />}
              onPress={onFiltersPress} // Use the handler
              testID="filters-button"
            >
              {screenT("recipe_inspiration.filters_button_text")}
            </Button>
          </View>
        </View>
      </View>
    ),
    [searchText, screenT, onFiltersPress /* Add other dependencies if needed */]
  );

  const loadingAnotherPage = !isLoadingFirstTime && isFetching && page > 1;

  const onEndReachedFunc = (): void => {
    if (data?.next && !isFetching) {
      // Check if more data exists and not already fetching
      // Basic pagination: increment page number.
      //   Needs refinement based on actual API response (e.g., parsing 'next' URL)
      // TODO: Parse page number from data.next URL for robust pagination
      setPage((prevPage) => prevPage + 1);
    }
  };

  const renderItemFunction = ({ item, index }: { item: InspirationRecipeTemplate; index: number }): JSX.Element => {
    // Create a mock GeneratedRecipeMeal object from RecipeTemplate
    // Spread RecipeTemplate properties (kcal, protein, etc.) to the top level

    const recipeMealForCard: GeneratedRecipeMeal = {
      // Top-level fields directly from item
      kcal: item.kcal,
      protein: item.protein,
      fat: item.fat,
      carbohydrates: item.carbohydrates,
      portions: item.portions ?? 1,
      fiber: item.fiber ?? 0, // Assuming fiber might exist or default to 0

      // Dummy arrays for complex types expected by GeneratedRecipeMeal but not used by Card
      ingredients: [], // Type: GeneratedRecipeMealIngredient[]
      directions: [], // Type: GeneratedRecipeMealDirection[]

      // Nested recipe_template, constructed to be RecipeTemplate compatible
      recipe_template: {
        // Fields directly from item that RecipeTemplate shares (add/remove as needed based on actual types)
        id: item.id,
        name: item.name,
        image: item.image,
        preparation_time_min: item.preparation_time_min,
        cooking_time_min: item.cooking_time_min,
        tags: item.tags,
        source_provider: item.source_provider,
        // Nutrition fields required by RecipeTemplate
        kcal: item.kcal,
        protein: item.protein,
        fat: item.fat,
        carbohydrates: item.carbohydrates,
        fiber: item.fiber,
        // Fields required by RecipeTemplate but potentially missing in InspirationRecipeTemplate
        // Provide defaults or empty values. Check RecipeTemplate definition!
        created: "", // Default
        modified: "", // Default
        ingredients: [], // Default: empty Ingredient[]
        directions: [], // Default: empty Direction[]
        // Add any other mandatory RecipeTemplate fields here
      } as RecipeTemplate, // We assert this structure matches RecipeTemplate for the card's needs
    };

    return (
      <RecipeMealCard
        recipeMeal={recipeMealForCard}
        mealSlotSpecification={dummyMealSlotSpecification} // Ensure dummy spec is passed here too
        onButtonPress={() => onRecipePress(item)}
        index={index}
      />
    );
  };

  // Memoized Empty State Component
  const ListEmptyComponent = useMemo(() => {
    const ListEmptyComponentInner = (): JSX.Element => (
      <Center flex={1} mt={Scale(20)}>
        {(() => {
          if (isLoadingFirstTime || (isFetching && page === 1)) {
            return <Spinner color="primary.600" />;
          }
          if (error) {
            return (
              <VStack space={2} alignItems="center">
                <Icon as={MaterialIcons} name="error-outline" size="lg" color="error.500" />
                <RNText style={[styles.errorText, { color: theme.colors.error[500] }]}>
                  {screenT("general.error_loading_data")}
                </RNText>
              </VStack>
            );
          }
          return <Text>{screenT("general.no_search_results")}</Text>;
        })()}
      </Center>
    );
    ListEmptyComponentInner.displayName = "ListEmptyComponentInner";
    return ListEmptyComponentInner;
  }, [isLoadingFirstTime, isFetching, page, error, screenT, theme.colors.error]);

  return (
    <SafeAreaView style={commonStyles.container} nativeID="recipeInspirationScreen">
      <FlatList
        ListHeaderComponent={headerComponent}
        ListFooterComponent={loadingAnotherPage ? <Spinner /> : null}
        data={allRecipes}
        renderItem={renderItemFunction}
        keyExtractor={(item) => String(item.id)} // Use RecipeTemplate id
        onEndReached={onEndReachedFunc}
        onEndReachedThreshold={0.5} // Adjust threshold as needed
        showsVerticalScrollIndicator={false}
        ListEmptyComponent={ListEmptyComponent} // Use the memoized empty component
        keyboardShouldPersistTaps="handled"
        testID={"recipeInspiration-list"}
      />

      {/* Render the Filter Actionsheet Component directly, passing props */}
      <FilterActionsheetComponent {...actionsheetProps} t={filterHookT} />
    </SafeAreaView>
  );
};

// Use StyleSheet.create for proper typing
const styles = StyleSheet.create({
  pageHeader: {
    flexDirection: "row",
    alignItems: "center",
    // Add padding/margin as needed
  },
  headerTitle: {
    textAlign: "center",
    flex: 1,
    paddingVertical: Scale(15),
  },
  searchContainer: {
    paddingTop: Scale(10),
  },
  filterButtonContainer: {
    marginVertical: Scale(15),
    alignItems: "flex-start", // Align button to the left
  },
  errorText: {
    // Add specific styling for error text if needed
    textAlign: "center",
    marginTop: Scale(5),
  },
  // Add other styles if necessary
});

export default RecipeInspirationScreen;
